import { useKeyPressWithHandler } from '@openeducation/oe-it-hooks';
import React, {
  CSSProperties,
  DetailedHTMLProps,
  TextareaHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from 'react';

import useAutosizeTextArea from './use-autosize-textarea';

type TextareaProps = DetailedHTMLProps<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  HTMLTextAreaElement
> & {
  value: string;
  style: CSSProperties | CSSProperties[];
  onChangeText: (value: string) => void;
  onSubmitEditing: (value: string) => void;
  testID?: string;
  returnKeyType: string; //NOT Used, only added for props compatibility with native
};

const spreadStyles = (style: CSSProperties[]) => {
  let spreadedStyles = {};

  style.forEach(
    (s) =>
      (spreadedStyles = {
        ...spreadedStyles,
        ...s,
      })
  );

  return spreadedStyles;
};

export const Textarea = ({
  value,
  style,
  onChangeText,
  onSubmitEditing,
  returnKeyType,
  testID,
  ...props
}: TextareaProps) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [internalValue, setInternalValue] = useState('');

  useAutosizeTextArea(textAreaRef.current, internalValue, 28);

  useEffect(() => setInternalValue(value), [value]);

  const keyPressHandler = React.useCallback(
    (event: KeyboardEvent) => {
      if (event.type !== 'keydown') {
        return;
      }

      if (event.key === 'Enter' && !event.shiftKey && onSubmitEditing) {
        onSubmitEditing?.(internalValue);
        event.preventDefault();
      }
    },
    [internalValue, onSubmitEditing]
  );
  useKeyPressWithHandler(keyPressHandler);

  const handleChange = (evt: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = evt.target?.value;

    setInternalValue(val);
    onChangeText?.(val);
  };

  return (
    <textarea
      data-testid={testID}
      rows={1}
      {...props}
      // eslint-disable-next-line react-native/no-inline-styles
      style={{
        minHeight: 17,
        ...(Array.isArray(style) ? spreadStyles(style) : style),
      }}
      onChange={handleChange}
      ref={textAreaRef}
      value={internalValue}
    />
  );
};
